import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  GraduationCap, PanelLeft, Search, ChevronDown, ChevronRight, ChevronsUpDown, X, Menu,
  LayoutDashboard, CalendarDays, PieChart, Users, BookOpen, Video, UserCheck, Settings,
  CheckSquare, ListTodo, Inbox, Target, Clock, DollarSign,
  CalendarClock, Shield, Crown, Moon, Sun, LogOut, UserCog,
} from 'lucide-react';
import { useAuth } from '../../hooks/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { db } from '../../services/firebase';
import NotificationBell from '../ui/NotificationBell';
import { COLOR_LEGEND } from '../../modules/calendar/utils/colorLegend';
import { isViewAsInstructor, asInstructorUser } from '../../utils/viewAs';

// ============================================================================
// Menú principal del Gestor, con el estilo del panel de beta.hingenia.com.
// Reemplaza a la barra superior (components/ui/RightSidebar.jsx, que se conserva
// sin usar). Gestor Pro, Agente IA y Generador están desactivados: no salen en el menú y
// sus URLs (/panel, /ia…) redirigen al Inicio (ver MODULOS_OCULTOS en App.js).
// ============================================================================

const LS_MINI = 'hx-side-mini';
const LS_GRUPOS = 'hx-side-grupos-cerrados';

const calendarPaths = ['/calendar', '/planning', '/instructors', '/courses', '/zoom-accounts', '/asesores', '/calendar-settings', '/dashboard'];

// Cada ítem: { id, label, icon, href, match?, perm?, sub? }.
// `perm` es la clave de user.appPermissions (admin siempre ve todo).
const buildNav = (user) => {
  const isAdmin = user?.role === 'admin';
  const perms = user?.appPermissions || {};
  const allowed = (perm) => isAdmin || !perm || perms[perm] !== false;
  const isInstructorOnly = user?.isInstructor && !user?.isCollaborator && !isAdmin;

  const calInstructor = {
    id: 'mi-calendario',
    label: user?.isInstructor ? 'Mi calendario' : 'Cal. instructor',
    icon: CalendarClock,
    href: '/mi-calendario',
  };

  if (isInstructorOnly) {
    return [{ items: [calInstructor] }];
  }

  const groups = [
    { items: [{ id: 'inicio', label: 'Inicio', icon: LayoutDashboard, href: '/inicio' }] },
    {
      label: 'Operación',
      items: [
        {
          id: 'calendario', label: 'Calendario', icon: CalendarDays, perm: 'calendario',
          match: (p) => calendarPaths.includes(p),
          sub: [
            { id: 'cal', label: 'Calendario', icon: CalendarDays, href: '/calendar' },
            { id: 'cal-plan', label: 'Planificación', icon: PieChart, href: '/planning' },
            { id: 'cal-inst', label: 'Instructores', icon: Users, href: '/instructors' },
            { id: 'cal-cursos', label: 'Cursos', icon: BookOpen, href: '/courses' },
            { id: 'cal-zoom', label: 'Cuentas Zoom', icon: Video, href: '/zoom-accounts' },
            { id: 'cal-ases', label: 'Asesores', icon: UserCheck, href: '/asesores' },
            { id: 'cal-ajustes', label: 'Ajustes', icon: Settings, href: '/calendar-settings' },
          ],
        },
        {
          id: 'tareas', label: 'Tareas', icon: CheckSquare, perm: 'tareas',
          match: (p) => p.startsWith('/tareas'),
          sub: [
            { id: 't-inicio', label: 'Proyectos', icon: LayoutDashboard, href: '/tareas', match: (p) => p === '/tareas' || p.startsWith('/tareas/lista') || p.startsWith('/tareas/espacio') },
            { id: 't-mias', label: 'Mis tareas', icon: ListTodo, href: '/tareas/mis-tareas' },
            { id: 't-inbox', label: 'Bandeja', icon: Inbox, href: '/tareas/inbox' },
            { id: 't-metas', label: 'Metas', icon: Target, href: '/tareas/metas' },
            { id: 't-ajustes', label: 'Ajustes', icon: Settings, href: '/tareas/ajustes' },
          ],
        },
      ],
    },
    {
      label: 'Equipo',
      items: [
        { id: 'horas', label: 'Horas extra', icon: Clock, href: '/horas-extras', perm: 'horasExtra' },
        ...((user?.isSeller || isAdmin) ? [{ id: 'ventas', label: 'Ventas', icon: DollarSign, href: '/sales-advisor', perm: 'ventas' }] : []),
        ...((user?.isInstructor || isAdmin || perms.calInstructor === true) ? [calInstructor] : []),
      ],
    },
  ];

  if (isAdmin) {
    groups.push({
      label: 'Administración',
      items: [
        { id: 'admin', label: 'Usuarios y permisos', icon: Shield, href: '/admin' },
        ...(user?.email === 'vcecursos@gmail.com' ? [{ id: 'super', label: 'Super admin', icon: Crown, href: '/super-admin' }] : []),
      ],
    });
  }

  return groups
    .map((g) => ({ ...g, items: g.items.filter((it) => allowed(it.perm)) }))
    .filter((g) => g.items.length > 0);
};

const isLeafActive = (item, path) => (item.match ? item.match(path) : path === item.href);

// Lista plana para el buscador Ctrl K.
const flatten = (groups) =>
  groups.flatMap((g) =>
    g.items.flatMap((it) =>
      it.sub ? it.sub.map((s) => ({ ...s, parent: it.label })) : [{ ...it, parent: g.label || '' }]
    )
  );

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const writeJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* sin storage */ }
};

const CommandPalette = ({ items, onClose, onGo }) => {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((i) => `${i.label} ${i.parent}`.toLowerCase().includes(t));
  }, [q, items]);

  useEffect(() => { setSel(0); }, [q]);

  const onKey = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter' && results[sel]) onGo(results[sel].href);
  };

  return (
    <div className="cmdk-scrim hx-ui" onMouseDown={onClose}>
      <div className="cmdk" onMouseDown={(e) => e.stopPropagation()}>
        <input autoFocus placeholder="Buscar o ir a…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} />
        {results.length === 0 ? (
          <div className="empty">Sin resultados para “{q}”</div>
        ) : (
          <ul>
            {results.map((r, i) => (
              <li key={r.id}>
                <button type="button" className={i === sel ? 'sel' : ''} onMouseEnter={() => setSel(i)} onClick={() => onGo(r.href)}>
                  <r.icon size={16} />
                  {r.label}
                  {r.parent && <small>{r.parent}</small>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const AppSidebar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  // "Ver como instructor": el admin ve el mismo menú reducido que el instructor.
  const viewAs = isViewAsInstructor(user, location);
  const groups = useMemo(() => buildNav(viewAs ? asInstructorUser(user) : user), [user, viewAs]);
  const flat = useMemo(() => flatten(groups), [groups]);

  const [mini, setMini] = useState(() => readJSON(LS_MINI, false));
  const [cerrados, setCerrados] = useState(() => readJSON(LS_GRUPOS, []));
  const [subs, setSubs] = useState({});
  const [pop, setPop] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const meRef = useRef(null);

  // Foto de perfil desde Firestore (pisa la de Google), igual que la barra anterior.
  useEffect(() => {
    if (!user?.uid) return undefined;
    return onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const data = snap.exists() ? snap.data() : null;
      if (data?.photoURL) setPhoto(data.photoURL);
    }, () => {});
  }, [user?.uid]);

  // Al navegar: cierra el cajón móvil y devuelve los submenús a su estado natural.
  useEffect(() => { setMobileOpen(false); setSubs({}); }, [path]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!pop) return undefined;
    const close = (e) => { if (meRef.current && !meRef.current.contains(e.target)) setPop(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pop]);

  const toggleMini = () => setMini((v) => { writeJSON(LS_MINI, !v); return !v; });
  const toggleGrupo = (label) => setCerrados((prev) => {
    const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
    writeJSON(LS_GRUPOS, next);
    return next;
  });

  const go = (href) => { setSearchOpen(false); navigate(href); };

  const handleLogout = async () => {
    try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
  };

  const leaf = (item) => {
    const on = isLeafActive(item, path);
    return (
      <Link key={item.id} to={item.href} className={`ni${on ? ' on' : ''}`} title={item.label} aria-current={on ? 'page' : undefined}>
        <item.icon size={16} />
        <span>{item.label}</span>
      </Link>
    );
  };

  const parent = (item) => {
    const childActive = item.sub.some((s) => isLeafActive(s, path)) || (item.match && item.match(path));
    const open = subs[item.id] ?? childActive;
    return (
      <div key={item.id}>
        <button
          type="button"
          className={`ni${open ? ' open' : ''}${mini && childActive ? ' on' : ''}`}
          title={item.label}
          aria-expanded={open}
          onClick={() => {
            if (mini) { navigate(item.sub[0].href); return; }
            setSubs((prev) => ({ ...prev, [item.id]: !open }));
          }}
        >
          <item.icon size={16} />
          <span>{item.label}</span>
          <ChevronRight size={14} className="chev" />
        </button>
        <div className={`sub${open ? ' open' : ''}`}>{item.sub.map(leaf)}</div>
      </div>
    );
  };

  const name = user?.displayName || user?.email?.split('@')[0] || 'Usuario';
  const initial = name.trim().charAt(0).toUpperCase();
  const avatarSrc = photo || user?.photoURL;
  const dark = theme === 'dark';
  const showLegend = path === '/calendar';

  return (
    <>
      {/* Barra móvil */}
      <div className="m-bar hx-ui">
        <button type="button" className="icon-btn" onClick={() => setMobileOpen(true)} aria-label="Abrir menú">
          <Menu size={20} />
        </button>
        <b>Hingenia</b>
        <span style={{ marginLeft: 'auto' }}><NotificationBell /></span>
      </div>
      {mobileOpen && <div className="m-scrim" onClick={() => setMobileOpen(false)} />}

      <aside className={`side hx-ui${mini ? ' mini' : ''}${mobileOpen ? ' drawer-open' : ''}`} aria-label="Menú principal">
        <div className="s-top">
          <div className="brand">
            <span className="logo"><GraduationCap size={18} /></span>
            <div>
              <b>Hingenia</b>
              <small>Gestor interno</small>
            </div>
            <button type="button" className="hx-collapse" onClick={toggleMini} title={mini ? 'Expandir menú' : 'Contraer menú'}>
              <PanelLeft size={16} />
            </button>
            <button type="button" className="hx-collapse hx-only-mobile" onClick={() => setMobileOpen(false)} title="Cerrar menú">
              <X size={16} />
            </button>
          </div>
          <button type="button" className="s-search" onClick={() => setSearchOpen(true)} title="Buscar o ir a… (Ctrl K)">
            <Search size={15} />
            <span>Buscar o ir a…</span>
            <kbd>Ctrl K</kbd>
          </button>
        </div>

        <nav className="s-nav">
          {groups.map((g, i) => {
            const key = g.label || `grupo-${i}`;
            const closed = !!g.label && cerrados.includes(g.label);
            return (
              <div key={key} className={`grp${closed ? ' closed' : ''}`}>
                {g.label && (
                  <button type="button" className="grp-h" onClick={() => toggleGrupo(g.label)} aria-expanded={!closed}>
                    {g.label}
                    <ChevronDown size={13} />
                  </button>
                )}
                <div className="grp-b">{g.items.map((it) => (it.sub ? parent(it) : leaf(it)))}</div>
              </div>
            );
          })}

          {/* La leyenda de colores vivía en el sidebar propio del Calendario. */}
          {showLegend && (
            <div className="legend">
              <p>Leyenda de colores</p>
              {COLOR_LEGEND.map((l) => (
                <div key={l.color}><i style={{ background: l.color }} />{l.label}</div>
              ))}
            </div>
          )}
        </nav>

        <div className="s-foot">
          <div className="foot-row">
            <NotificationBell placement="up" />
            <button type="button" className="icon-btn" onClick={() => toggleTheme(dark ? 'light' : 'dark')} title={dark ? 'Modo claro' : 'Modo oscuro'}>
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
          <div
            ref={meRef}
            className="me"
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={pop}
            onClick={() => setPop((v) => !v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPop((v) => !v); } }}
          >
            {avatarSrc ? <img className="av" src={avatarSrc} alt="" /> : <span className="av">{initial}</span>}
            <div className="who">
              <b>{name}</b>
              <small>{user?.email}</small>
            </div>
            <ChevronsUpDown size={15} className="dots" />
            <div className={`pop${pop ? ' open' : ''}`} role="menu" onClick={(e) => e.stopPropagation()}>
              <button type="button" role="menuitem" onClick={() => { setPop(false); navigate('/settings'); }}>
                <UserCog size={16} /> Mi perfil y ajustes
              </button>
              <button type="button" role="menuitem" onClick={() => { setPop(false); toggleTheme(dark ? 'light' : 'dark'); }}>
                {dark ? <Sun size={16} /> : <Moon size={16} />} {dark ? 'Modo claro' : 'Modo oscuro'}
              </button>
              <hr />
              <button type="button" role="menuitem" onClick={handleLogout} style={{ color: 'var(--bad)' }}>
                <LogOut size={16} /> Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </aside>

      {searchOpen && <CommandPalette items={flat} onClose={() => setSearchOpen(false)} onGo={go} />}
    </>
  );
};

export default AppSidebar;
